<template>
  <form @submit.prevent="submitForm">
    <div class="product__size">
      <SizePickerSuit v-if="sizePickerType === SizePickerTypes.SizePickerSuit" v-bind="sizePickerSuitProps" />
      <SizePickerPants v-if="sizePickerType === SizePickerTypes.SizePickerPants" v-bind="sizePickerPantsProps" />
      <SizePickerRegular v-if="sizePickerType === SizePickerTypes.SizePickerRegular" v-bind="sizePickerRegularProps" />
    </div>
    <div v-if="isProductInStock" v-auto-animate class="product__add">
      <input type="hidden" value="1" :name='$t("skuParameterCode")' />
      <button class="btn btn--profile btn--inset u-display-block btn--add-to-cart product__add-to-cart-btn"
        type="submit">
        {{ $t("addToBasket") }}
      </button>
      <FeedbackMessage :message="showClickAndCollectStockError ? `${$t('notAvailableForClickAndCollect')} ${clickAndCollectLocationStore.storeName}` : addToCartError" type="error" />
    </div>

    <!-- Disabled Add to cart button for out-of-stock case -->
    <div v-else class="product__add">
      <button class="btn btn--add-to-cart u-display-block product__add-to-cart-btn" type="submit" disabled>
        {{ $t("addToBasket") }}
      </button>
    </div>

    <!--Product availability information-->
    <!-- Show availability only if shopId is not Jean Paul and showAvailability prop is true-->
    <div v-if="props.shopId != 'JeanPaul' && props.showAvailability" class="product__availability">
      <!-- Online availability -->
      <div v-if="isProductInStock" class="product__availability__text">
        <span class="product__availability-dot product__availability-dot--success"></span>
        <p>
          {{ $t("availableOnline") }}
        </p>
      </div>
      <div v-else class="product__availability__text">
        <span class="product__availability-dot product__availability-dot--error"></span>
        <p>
          {{ $t("soldoutOnline") }}
        </p>
      </div>

      <!-- Store availability with count -->
      <div v-if="displayStoreCount > 0" class="product__availability-link product__availability__instore-text">
        <span class="product__availability-dot product__availability-dot--success"></span>
        <button type="button" class="btn--link _js-vue-component-toggler" data-togglewhat="storeInventory">
          <p>
            {{ $t("availableInXStores") }} {{ displayStoreCount }} {{ $t("stores") }}
          </p>
        </button>
      </div>
      <div v-else class="product__availability__text">
        <span class="product__availability-dot product__availability-dot--error"></span>
        <p>
          {{ $t("notAvailableInStores") }}
        </p>
      </div>
    </div>
    <!-- Click-and-collect availablity -->
      <FeedbackMessage v-if="productAvailableInSelectedStore && productPageStore.clickAndCollectEnabled && clickAndCollectLocationStore.storeName" class="feedback-message--align-start" :message="`${$t('clickAndCollectAvailable')} ${clickAndCollectLocationStore.storeName}`" type="success" icon="pickup-available" icon-size="small" />
  </form>
  <CartStatus v-if="props.checkoutLink && props.displayName && props.cartStatusImage" :display-name="props.displayName"
    :item-color="props.itemColor" :item-meta-color="props.itemMetaColor" :cart-status-image="props.cartStatusImage"
    :checkout-link="props.checkoutLink" :esalesKey="props.esalesKey" :categoryName="props.categoryName" />
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch, withDefaults } from "vue";
import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { vueInventory } from "../../../../global/modules/inventory_wp";
import { vueComponentToggler } from "../../../../global/modules/toggler_wp";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import Icon from "../../components/icon/icon.vue";
import { resolveOrderTypeWarehouseCode } from "../../composables/resolveOrderTypeWarehouseCode";
import { VoyadoEngageWebTrackingService } from "../../services/voyadoEngageWebTracking.service";
import { useProductPageStore } from "../../stores/productPageStore";
import { validateSizeSelection } from "../../utils/productSizeValidation";
import CartStatus from "../../views/cartStatus/cartStatus.vue";
import FeedbackMessage from '../shoppingCart/FeedbackMessage.vue';
import { useClickAndCollectLocationStore } from '../shoppingCart/stores/clickAndCollectLocationStore';
import { useOrderTypeStore } from '../shoppingCart/stores/orderTypeStore';
import { useShoppingCartStore } from '../shoppingCart/stores/shoppingCartStore';
import { AddToCartFormProps, StoreInventoryItem } from "./addToCartForm.models";
import SizePickerPants from "./sizePickerPants.vue";
import SizePickerRegular from "./sizePickerRegular.vue";
import SizePickerSuit from "./sizePickerSuit.vue";

const productPageStore = useProductPageStore();
const shoppingCartStore = useShoppingCartStore();
const clickAndCollectLocationStore = useClickAndCollectLocationStore();
const orderTypeStore = useOrderTypeStore();

const props = withDefaults(defineProps<AddToCartFormProps>(), {
  isSizeGuide: false,
  showInventoryNotification: false,
  showAvailability: false,
  isQuickAdd: false,
});

const formSubmitted = ref(false);
const addToCartError = ref('');
const isStockError = ref(false);

// True when a failed add was caused by insufficient click & collect stock; the template
// (where $t is available) then shows a store-specific message instead of the generic one.
const showClickAndCollectStockError = computed(() =>
  isStockError.value && orderTypeStore.orderType === 'clickAndCollect' && !!clickAndCollectLocationStore.storeName
);

// Store inventory data from window.jsonInventory
const storeInventory = ref<StoreInventoryItem[]>([]);

// Computed: total stores with any stock
const totalStoreCount = computed(() => {
  return storeInventory.value.length;
});

// Does this store's size list carry the given SKU? Curried so it composes directly into filter/find.
const hasSkuInStock = (skuCode: string) => (store: StoreInventoryItem): boolean =>
  !!store.sizes?.some(size => size.skuCode === skuCode);

// Computed: stores with selected SKU in stock
const storeCountForSelectedSku = computed(() => {
  const selectedSku = productPageStore.skuCode;
  if (!selectedSku) return totalStoreCount.value;

  return storeInventory.value.filter(hasSkuInStock(selectedSku)).length;
});

// Computed: display count (SKU-specific if selected, otherwise total)
const displayStoreCount = computed(() => {
  return productPageStore.skuCode ? storeCountForSelectedSku.value : totalStoreCount.value;
});

// Computed: is the product in stock at the selected click-and-collect store for the selected size
const productAvailableInSelectedStore = computed(() => {
  const selectedSku = productPageStore.skuCode;
  if (!selectedSku) return false;

  const selectedStore = storeInventory.value.find(store => store.id === clickAndCollectLocationStore.warehouseCode);
  if (!selectedStore) return false;

  return hasSkuInStock(selectedSku)(selectedStore);
});

// Computed: online availability (SKU-specific if selected, otherwise initial prop value)
const isProductInStock = computed(() => {
  if (!productPageStore.skuCode) {
    return props.isProductInStock;
  }
  return parseInt(productPageStore.inventoryCount) > 0;
});

const SizePickerTypes = {
  SizePickerSuit: "suit",
  SizePickerPants: "pants",
  SizePickerRegular: "regular",
} as const;

const sizePickerType = computed(() => {
  try {
    if (props.matrixSizes.length > 0) {
      const firstSize = props.matrixSizes[0][0].name ?? "";
      return isSuitSize(firstSize)
        ? SizePickerTypes.SizePickerSuit
        : SizePickerTypes.SizePickerPants;
    }

    return SizePickerTypes.SizePickerRegular;
  } catch (error) {
    console.error("Error parsing matrixSizes:", error);
    return SizePickerTypes.SizePickerRegular;
  }
});

// Function to determine if it's a suit size
const isSuitSize = (sizeName: string): boolean => {
  return /^[BC]\d+$/.test(sizeName); // Starts with B or C followed by digits
};

const fewItemsLeftThreshold = computed(() => {
  return parseInt('$t("fewItemsLeftThreshold")');;
});

const sizePickerRegularProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    regularSizes: props.regularSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const sizePickerPantsProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    matrixSizes: props.matrixSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const sizePickerSuitProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    matrixSizes: props.matrixSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const validateSelection = () => {
  return validateSizeSelection(sizePickerType.value, productPageStore);
};

async function checkStockStatus(
  count: string,
  treshold: number = 0,
): Promise<void> {
  const inventoryIsLow = parseInt(count) <= treshold;

  if (inventoryIsLow) {
    productPageStore.inventoryIsLow = true;
  } else {
    productPageStore.inventoryIsLow = false;
  }

  productPageStore.setCheckInventoryStatus(false);
}

const submitForm = async () => {
  addToCartError.value = '';
  isStockError.value = false;

  if (!validateSelection()) {
    return;
  }

  const warehouseCode = await resolveOrderTypeWarehouseCode(orderTypeStore.orderType);

  const result = await shoppingCartStore.addProduct(
    productPageStore.skuCode,
    props.isQuickAdd,
    props.itemListId || '',
    props.itemListName || '',
    productPageStore.apptusTicket,
    warehouseCode,
  );

  if (!result.success) {
    addToCartError.value = result.errorMessage ?? '';
    isStockError.value = result.isStockError ?? false;
    return;
  }

  if (!props.isQuickAdd) {
    productPageStore.setCartStatusIsOpen(true);
  }

  formSubmitted.value = true;

  if (props.addToCartCallback) {
    props.addToCartCallback();
  }
};

onBeforeMount(() => {
  if (props.isQuickAdd) { productPageStore.resetState(); }
});

onUnmounted(() => {
  productPageStore.resetState();

  // Scroll to the last added lineItem only if the form is submitted
  if (formSubmitted.value) {
    const checkoutLineItems = document.querySelectorAll(".checkout-line-item");

    if (checkoutLineItems.length > 0) {
      const lastCheckoutLineItem = checkoutLineItems[checkoutLineItems.length - 1];

      lastCheckoutLineItem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
});

onMounted(() => {
  vueInventory();

  // Read store inventory from global variable
  if ((window as any).jsonInventory) {
    storeInventory.value = (window as any).jsonInventory;
    // Init toggler after Vue renders the conditionally-shown button
    nextTick(() => vueComponentToggler());
  }

  productPageStore.setItemColor(props.itemColor ?? "");
  productPageStore.setItemMetaColor(props.itemMetaColor ?? "");
  productPageStore.setDisplayName(props.displayName ?? "");
  productPageStore.setCartStatusIsOpen(false);
  productPageStore.setClickAndCollectEnabled(props.clickAndCollectEnabled ?? false);

  if (productPageStore.clickAndCollectEnabled && !clickAndCollectLocationStore.isLoaded) {
    clickAndCollectLocationStore.fetchStore();
  }

  VoyadoEngageWebTrackingService.trackProductView({
    itemId: props.esalesKey,
    categoryName: decodeURIComponent(props.categoryName.replace(/\+/g, " "))
  })
});

watch(
  () => productPageStore.checkInventoryStatus,
  (newValue) => {
    if (newValue === true) {
      // Trigger the API call with the inventory count value
      checkStockStatus(
        productPageStore.inventoryCount,
        fewItemsLeftThreshold.value,
      );
    }
  },
);

// Selecting a different size should not carry over a stale add-to-cart error from the previous one.
watch(
  () => productPageStore.skuCode,
  () => {
    addToCartError.value = '';
    isStockError.value = false;
  },
);

</script>
